import * as layout from '../../components/markLayout.module.css';
import { MarkButton } from '../../components/MarkControls';
import React, { useId, useState } from 'react';
import { useParams, Link, useNavigate, Outlet } from 'react-router-dom';
import Section from '@hero/ui-kit/layout/Section';
import H from '@hero/ui-kit/typography/H';
import P from '@hero/ui-kit/typography/P';
import Tag from '@hero/ui-kit/components/Tag';
import BackButton from '../../../../components/BackButton';
import InlineLoader from '../../../../components/InlineLoader';
import QueryInlineError from '../../../../components/QueryInlineError';
import useSegment from '../../api/useSegment';
import useSegmentPreview from '../../api/useSegmentPreview';
import useBack from '../../../../hooks/useBack';
import SegmentUsedByList from './SegmentUsedByList';
import PreviewPanel from './SegmentBuilder/PreviewPanel';
import { stripEndOfDay } from './SegmentBuilder/validation';
import { getSegmentErrorCode } from './segmentErrors';
import { SEGMENT_ERROR_LABELS } from '../../constants';
import type { SegmentFilter, SegmentPreviewResponse } from '../../types';
type DetailTab = 'filters' | 'used_by';
const TABS: {
    key: DetailTab;
    label: string;
}[] = [
    { key: 'filters', label: 'Filters' },
    { key: 'used_by', label: 'Used by' }
];
const FilterDisplay: React.FC<{
    filter: SegmentFilter;
}> = ({ filter }) => (
    <div
        style={{
            marginBottom: '0.4rem',
            backgroundColor: 'var(--mark-surface)'
        }}
        className={layout.panel}
    >
        <div
            style={{
                display: 'flex',
                gap: '0.8rem',
                alignItems: 'center',
                flexWrap: 'wrap'
            }}
        >
            <P
                noDefaultMargin
                strong
                styles={{
                    minWidth: '12rem'
                }}
            >
                {filter.attribute}
            </P>
            <Tag type="warn" size="small">
                {filter.operator}
            </Tag>
            <P
                noDefaultMargin
                size="small"
                styles={{
                    color: 'var(--mark-muted)'
                }}
            >
                {filter.values
                    .map((v) => (typeof v === 'string' ? stripEndOfDay(v) : String(v)))
                    .join(', ') || '—'}
            </P>
        </div>
    </div>
);
const SegmentDetail: React.FC = () => {
    const { segmentId } = useParams<{
        segmentId: string;
    }>();
    const back = useBack();
    const navigate = useNavigate();
    const tabId = useId();
    const [activeTab, setActiveTab] = useState<DetailTab>('filters');
    const [previewData, setPreviewData] = useState<SegmentPreviewResponse | null>(null);
    const numericId = Number(segmentId);
    const { data: segmentResponse, isLoading, error } = useSegment(numericId);
    const previewMutation = useSegmentPreview();
    const segment = segmentResponse?.data;
    const filters = Array.isArray(segment?.filters) ? segment.filters : null;
    const previewErrorCode = previewMutation.error
        ? getSegmentErrorCode(previewMutation.error)
        : null;
    const previewError = previewMutation.error
        ? previewErrorCode
            ? (SEGMENT_ERROR_LABELS[previewErrorCode] ?? previewErrorCode)
            : 'Preview failed'
        : null;
    const handlePreview = () => {
        previewMutation.mutate(
            { segment_id: numericId },
            {
                onSuccess: (response) => setPreviewData(response.data),
                onError: () => setPreviewData(null)
            }
        );
    };
    const activateTab = (index: number) => {
        const tab = TABS[index];
        setActiveTab(tab.key);
        requestAnimationFrame(() => document.getElementById(`${tabId}-tab-${tab.key}`)?.focus());
    };
    const handleTabKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
        let nextIndex: number | null = null;
        if (event.key === 'ArrowRight') nextIndex = (index + 1) % TABS.length;
        if (event.key === 'ArrowLeft') nextIndex = (index - 1 + TABS.length) % TABS.length;
        if (event.key === 'Home') nextIndex = 0;
        if (event.key === 'End') nextIndex = TABS.length - 1;
        if (nextIndex === null) return;
        event.preventDefault();
        activateTab(nextIndex);
    };
    if (isLoading) return <InlineLoader />;
    if (error && !segment) return <QueryInlineError error={error} />;
    if (!segment) return <P>Segment not found.</P>;
    return (
        <Section subtheme={['regular', 'background']} className={layout.page} noDefaultPadding>
            <BackButton onBack={() => back('/mark/segments')} alt="Back to segments" />
            <P
                noDefaultMargin
                size="small"
                styles={{
                    margin: '0.8rem 0',
                    color: 'var(--mark-muted)'
                }}
            >
                <Link
                    to="/mark"
                    style={{
                        color: 'inherit'
                    }}
                >
                    Mark
                </Link>
                {' > '}
                <Link
                    to="/mark/segments"
                    style={{
                        color: 'inherit'
                    }}
                >
                    Segments
                </Link>
                {' > '}
                {segment.name}
            </P>
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    flexWrap: 'wrap',
                    gap: '1.6rem',
                    marginBottom: '1.6rem'
                }}
            >
                <div>
                    <H role="h4" noDefaultMargin>
                        {segment.name}
                    </H>
                    <div
                        style={{
                            display: 'flex',
                            gap: '0.8rem',
                            alignItems: 'center',
                            marginTop: '0.4rem'
                        }}
                    >
                        <Tag type={segment.kind === 'QUERY' ? 'primary' : 'warn'} size="small">
                            {segment.kind}
                        </Tag>
                        <P
                            noDefaultMargin
                            size="small"
                            styles={{
                                color: 'var(--mark-muted)'
                            }}
                        >
                            Target: {segment.target_kind}
                        </P>
                        <P
                            noDefaultMargin
                            size="small"
                            styles={{
                                color: 'var(--mark-muted)'
                            }}
                        >
                            Match: {segment.match}
                        </P>
                        <P
                            noDefaultMargin
                            size="small"
                            styles={{
                                color: 'var(--mark-muted)'
                            }}
                        >
                            Matches: {segment.match_count.toLocaleString()}
                        </P>
                        <P
                            noDefaultMargin
                            size="small"
                            styles={{
                                color: 'var(--mark-muted)'
                            }}
                        >
                            Created: {new Date(segment.created).toLocaleDateString()}
                        </P>
                    </div>
                    {segment.source_segment_name && (
                        <P
                            noDefaultMargin
                            size="small"
                            styles={{
                                color: 'var(--mark-muted)',
                                marginTop: '0.4rem'
                            }}
                        >
                            Chained from: {segment.source_segment_name}
                        </P>
                    )}
                </div>
                <div
                    style={{
                        display: 'flex',
                        gap: '0.8rem'
                    }}
                >
                    {segment.kind === 'QUERY' && (
                        <MarkButton
                            type="button"
                            disabled={filters === null}
                            title={
                                filters === null
                                    ? 'Filter information must be available before editing.'
                                    : undefined
                            }
                            onClick={() => navigate(`/mark/segments/${segment.segment_id}/edit`)}
                            variant="primary"
                        >
                            Edit
                        </MarkButton>
                    )}
                    {segment.kind === 'IMPORTED' && (
                        <MarkButton
                            type="button"
                            onClick={() =>
                                navigate(`/mark/segments/import?segment_id=${segment.segment_id}`)
                            }
                        >
                            Replace via CSV
                        </MarkButton>
                    )}
                </div>
            </div>
            <PreviewPanel
                data={previewData}
                isLoading={previewMutation.isPending}
                error={previewError}
                onPreview={handlePreview}
                disabled={false}
                targetKind={segment.target_kind}
            />
            <div
                role="tablist"
                aria-label="Segment details"
                style={{
                    borderBottom: '2px solid var(--mark-border)',
                    marginBottom: '1.6rem'
                }}
                className={layout.tabs}
            >
                {TABS.map((tab, index) => (
                    <MarkButton
                        key={tab.key}
                        type="button"
                        role="tab"
                        id={`${tabId}-tab-${tab.key}`}
                        aria-selected={activeTab === tab.key}
                        aria-controls={`${tabId}-panel-${tab.key}`}
                        tabIndex={activeTab === tab.key ? 0 : -1}
                        onClick={() => setActiveTab(tab.key)}
                        onKeyDown={(event) => handleTabKeyDown(event, index)}
                        style={{
                            marginBottom: '-2px'
                        }}
                    >
                        {tab.label}
                    </MarkButton>
                ))}
            </div>
            <div
                role="tabpanel"
                id={`${tabId}-panel-filters`}
                aria-labelledby={`${tabId}-tab-filters`}
                hidden={activeTab !== 'filters'}
                tabIndex={0}
            >
                {filters === null ? (
                    <P noDefaultMargin styles={{ color: 'var(--mark-muted)' }}>
                        {segment.kind === 'IMPORTED'
                            ? 'Imported segments use a CSV contact list instead of filter rules.'
                            : 'Filter information is unavailable for this segment.'}
                    </P>
                ) : filters.length === 0 ? (
                    <P
                        styles={{
                            padding: '2rem 0',
                            textAlign: 'center',
                            color: 'var(--mark-muted)'
                        }}
                    >
                        No filters.
                    </P>
                ) : (
                    filters.map((filter, index) => <FilterDisplay key={index} filter={filter} />)
                )}
            </div>
            <div
                role="tabpanel"
                id={`${tabId}-panel-used_by`}
                aria-labelledby={`${tabId}-tab-used_by`}
                hidden={activeTab !== 'used_by'}
                tabIndex={0}
            >
                <SegmentUsedByList usedBy={segment.used_by} />
            </div>
            <Outlet />
        </Section>
    );
};
export default SegmentDetail;
